import React from "react";
import { StandardCard } from "../../components/Cards";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  SelectProps,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomSelectorWithLabel,
} from "../../components/formsComponents";
import moment from "moment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useFormik } from "formik";
import { useDispatchWrapper } from "../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../redux/messages/messagesActions";
import { api } from "../../api/api";
import { ViewOccupancyRightPanel } from "./ViewOccupancy";
import { PinDrop, Visibility } from "@mui/icons-material";
import { VenueAutoSearch } from "../../components/AutoCompleteSearches/VenueAutoSearch";
import { useBillingCompanyContext } from "../../providers";
import { NoRecordFound } from "../../components/NoRecordFound/NoRecordFound";
import { ILead } from "../../redux/leads/leads.types";
import { ViewOngoingLeadDialog } from "./dialogs/ViewOngoingLeadDialog";

export const CustomTableContainer = styled(TableContainer)({
  overflow: "auto",

  /* Custom scrollbar for WebKit browsers (Chrome, Safari) */
  "&::-webkit-scrollbar": {
    width: 8 /* Width of the vertical scrollbar */,
    height: 8 /* Height of the horizontal scrollbar */,
  },

  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1" /* Background of the scrollbar track */,
  },

  "&::-webkit-scrollbar-thumb": {
    background: "#888" /* Color of the scrollbar thumb */,
    borderRadius: 12 /* Rounded corners of the scrollbar thumb */,
  },

  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555" /* Color of the scrollbar thumb on hover */,
  },

  /* Custom scrollbar for Firefox */
  scrollbarWidth: "thin" /* Thin scrollbar */,
  scrollbarColor: "#4e4e4e #222222" /* Thumb color, Track color */,

  /* Optional: Style the scrollbar corner for WebKit browsers */
  "&::-webkit-scrollbar-corner": {
    background: "#f1f1f1" /* Background of the scrollbar corner */,
  },
});

export const CustomTableCell = styled(TableCell)({
  border: "1px solid #404040",
  background: "#303030",
  color: "#fff",
});

export const CustomHeaderTableCell = styled(TableCell)({
  border: "1px solid #404040",
  background: "#222222",
  color: "#fff",
});

export const months_list = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

export const getYearsList = () => {
  const yersList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i < currentYear + 10; i++) {
    yersList.push(i);
  }
  return yersList;
};

/** Inclusive start year, exclusive end year — expands to include anchorYear when provided. */
function getYearOptionsRange(anchorYear?: number | null) {
  const cy = new Date().getFullYear();
  let start = cy;
  let endExclusive = cy + 10;
  if (anchorYear != null && Number.isFinite(anchorYear)) {
    start = Math.min(start, anchorYear);
    endExclusive = Math.max(endExclusive, anchorYear + 1);
  }
  return Array.from({ length: endExclusive - start }, (_, i) => start + i);
}

function getDaysInMonth(year: number, month: number) {
  // Create a date object for the next month and set the day to 0
  // This gets the last day of the desired month
  const days = new Date(year, month, 0).getDate();
  return days;
}

// function updateShiftStatus(data: any) {
//   const finalData = { ...data };
//   for (const date in finalData) {
//     // Remove DAY and check if it was BOOKED
//     let hasFullDayBooked = false;
//     finalData[date] = finalData[date].filter((shift: any) => {
//       if (shift.shift_time === "DAY" && shift.status === "BOOKED") {
//         hasFullDayBooked = true;
//       }
//       return shift.shift_time !== "DAY";
//     });

//     // If DAY was BOOKED, update remaining shifts' status to BOOKED
//     if (hasFullDayBooked) {
//       finalData[date] = finalData[date].map((shift: any) => ({
//         ...shift,
//         status: "BOOKED",
//       }));
//     }
//   }

//   return finalData;
// }

function updateShiftStatus(data: any) {
  // Clone the data to avoid mutation
  const finalData = { ...data };

  for (const date in finalData) {
    // Filter out the DAY shift if you want to treat it separately,
    // but it should NOT affect other shifts
    finalData[date] = finalData[date].map((shift: any) => {
      if (shift.shift_time === "DAY") {
        // Keep DAY shift as is
        return { ...shift };
      }
      // Other shifts remain unchanged
      return { ...shift };
    });
  }

  return finalData;
}

export const groupDataByMonth = (objDataWithKeys: any) => {
  const keysArray = Object.keys(objDataWithKeys);
  return keysArray.reduce((acc: any, date) => {
    const [year, month] = date.split("-");
    const key = `${year}-${month}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(date);
    return acc;
  }, {});
};

function groupByVenue(data: any) {
  const groupedData: any = {};

  Object.keys(data).forEach((date) => {
    data[date].forEach((item: any) => {
      const uuid = item.venue_uuid;
      if (!groupedData[uuid]) {
        groupedData[uuid] = { venue_name: item.venue_name, _fromCalendar: !item.occupancy_uuid, dates: {} };
      } else if (!groupedData[uuid]._fromCalendar && !item.occupancy_uuid) {
        // Prefer venue_name from calendar items (no occupancy_uuid) as they reflect current master
        groupedData[uuid].venue_name = item.venue_name;
        groupedData[uuid]._fromCalendar = true;
      }
      if (!groupedData[uuid].dates[date]) {
        groupedData[uuid].dates[date] = [];
      }
      groupedData[uuid].dates[date].push(item);
    });
  });

  // Clean up internal tracking field
  Object.values(groupedData).forEach((v: any) => delete v._fromCalendar);

  return groupedData;
}

export type ICustomSelectProps = {
  options: { label: number | string | null; value: string | number }[];
  helperText?: string;
} & SelectProps;

export interface IInventoryColumnData {
  occupancy_uuid: any;
  event_name: string;
  event_timing_uuid: string;
  venue_uuid: string;
  venue_name: string;
  calendar_uuid: string;
  calendar_date: string;
  status: string;
}

interface IInvetryDateSelect {
  function_date: string;
  function_month: string;
  event_time_uuid: string;
  event_time: string;
  preferred_venue_uuid: string;
  preferred_venue: string;
}

interface IInventoryProps {
  isFromEventLead?: boolean;
  eventLeadData?: any;
  /** YYYY-MM-DD (or parseable by moment). When set, overrides eventLeadData.function_date for default month/year, scroll, and column highlight. */
  calendarFocusDate?: string | null;
  onSelectevent?: (evt: IInvetryDateSelect) => void;
}
export const Inventory: React.FC<IInventoryProps> = (props) => {
  const { isFromEventLead, eventLeadData, calendarFocusDate, onSelectevent } =
    props;
  // ✅ Popover state
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedCell, setSelectedCell] = React.useState<any | null>(null);

  const openOngoingPopup = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedCell(null);
  };

  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const window_url = window.location.href;
  const tableRef = React.useRef<HTMLDivElement | null>(null);

  // State to track which venues are expanded (by default all are collapsed)
  const [expandedVenues, setExpandedVenues] = React.useState<Set<string>>(
    new Set(),
  );
  const [inventoryData, setInventoryData] = React.useState<any | null>([]);
  const [openView, setOpenView] = React.useState<string | null>(null);
  const [openOngoingDialog, setOpenOngoingDialog] = React.useState<
    ILead[] | null
  >(null);

  const eventFocusDateKey = React.useMemo(() => {
    const raw =
      calendarFocusDate != null && String(calendarFocusDate).trim() !== ""
        ? calendarFocusDate
        : isFromEventLead
          ? eventLeadData?.function_date
          : null;
    if (!raw) return null;
    const m = moment(raw);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
  }, [calendarFocusDate, isFromEventLead, eventLeadData?.function_date]);

  const yearOptionsList = React.useMemo(() => {
    const anchor =
      eventFocusDateKey != null ? moment(eventFocusDateKey).year() : null;
    return getYearOptionsRange(anchor);
  }, [eventFocusDateKey]);

  const inventoryInitialValues = React.useMemo(
    () => ({
      venueId:
        eventLeadData && eventLeadData.preferred_venue_uuid
          ? eventLeadData.preferred_venue_uuid
          : "",
      month:
        eventFocusDateKey != null
          ? moment(eventFocusDateKey).format("MM")
          : moment().format("MM"),
      year:
        eventFocusDateKey != null
          ? moment(eventFocusDateKey).format("YYYY")
          : moment().format("YYYY"),
    }),
    [eventLeadData?.preferred_venue_uuid, eventFocusDateKey],
  );

  const {
    values,
    errors,
    setFieldValue,
    handleChange,
    handleSubmit,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: inventoryInitialValues,
    validate: (values) => {
      const errors: any = {};
      if (!values.month) {
        errors.month = "Month is required!";
      } else if (!values.year) {
        errors.year = "Year is required!";
      }
      return errors;
    },
    onSubmit: async (values) => {
      await fetchOccupancyData(values);
    },
  });

  const fetchOccupancyData = async (params: {
    venueId: string;
    month: string;
    year: string;
  }) => {
    try {
      dispatch(saveLoaderProgress());
      const days = getDaysInMonth(Number(params.year), Number(params.month));
      let searchableUrl = `/occupancy/get-occupancy?company_outlet_uuid=${company_outlet_uuid}&from_date=${params.year}-${params.month}-01&to_date=${params.year}-${params.month}-${days}`;
      if (params.venueId) {
        searchableUrl += `&venue_uuid=${params.venueId}`;
      }

      const res = await api.get(searchableUrl);
      const responseData = res.data.data;

      const finalData = updateShiftStatus(responseData);
      const grouppedData = groupByVenue(finalData);
      setInventoryData(grouppedData);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

  const handleClearFilters = () => {
    resetForm({
      values: {
        venueId: "",
        month:
          eventFocusDateKey != null
            ? moment(eventFocusDateKey).format("MM")
            : moment().format("MM"),
        year:
          eventFocusDateKey != null
            ? moment(eventFocusDateKey).format("YYYY")
            : moment().format("YYYY"),
      },
    });
  };

  /** Single source of truth for status colors — used by both the legend and the grid cells. */
  const statusColors = React.useMemo(
    () => ({
      AVAILABLE: "#303030",
      BOOKED: theme.palette.error.main,
      HOLD: theme.palette.info.main,
      TENTATIVE_HOLD: "#8E44AD",
      ONGOING: theme.palette.warning.main,
    }),
    [theme.palette],
  );

  const getStatusColor = (status?: string) =>
    statusColors[status as keyof typeof statusColors] ?? statusColors.AVAILABLE;

  /** Light fills (e.g. ongoing yellow) need dark text to stay readable. */
  const getStatusTextColor = (status?: string) =>
    status === "ONGOING" ? "#000" : "#fff";

  /** Focused date is outlined (not filled) so it is never mistaken for a status color. */
  const focusOutline = `2px solid ${theme.palette.common.white}`;

  const getFocusColumnHeaderSx = React.useCallback(
    (dateKey: string) => {
      if (!eventFocusDateKey || dateKey !== eventFocusDateKey) return {};
      return {
        borderLeft: focusOutline,
        borderRight: focusOutline,
        fontWeight: 700,
      };
    },
    [eventFocusDateKey, focusOutline],
  );

  const getFocusColumnCellSx = React.useCallback(
    (dateKey: string, _baseColor?: string) => {
      if (!eventFocusDateKey || dateKey !== eventFocusDateKey) return {};
      return { borderLeft: focusOutline, borderRight: focusOutline };
    },
    [eventFocusDateKey, focusOutline],
  );

  React.useEffect(() => {
    // Fetch initial data when component mounts
    if (values.month && values.year) {
      fetchOccupancyData(values);
    }
  }, [values]);

  React.useEffect(() => {
    if (window_url.includes("venue_uuid")) {
      const urlParams = new URLSearchParams(window.location.search);
      const venue_uuid = urlParams.get("venue_uuid");
      const from_date = urlParams.get("from_date");
      const to_date = urlParams.get("to_date");
      const year = urlParams.get("year");
      const company_outlet_uuid = urlParams.get("company_outlet_uuid");

      if (venue_uuid && from_date && to_date && year && company_outlet_uuid) {
        (async () => {
          try {
            let searchableUrl = `/occupancy/get-occupancy?company_outlet_uuid=${company_outlet_uuid}&from_date=${from_date}&to_date=${to_date}`;
            if (venue_uuid) {
              searchableUrl += `&venue_uuid=${venue_uuid}`;
            }
            dispatch(saveLoaderProgress());
            const res = await api.get(searchableUrl);
            const responseData = res.data.data;

            const finalData = updateShiftStatus(responseData);
            const grouppedData = groupByVenue(finalData);
            setInventoryData(grouppedData);
          } catch (err: any) {
            dispatch(
              showMessage({
                type: "error",
                message: err.response.data.message,
                displayAs: "snackbar",
              }),
            );
          } finally {
            dispatch(saveLoaderCompleted());
          }
        })();
      }
    }
  }, [window_url]);

  const hardcodedShifts = [
    { label: "Full Day", match: "DAY" },
    { label: "Breakfast", match: "MORNING" },
    { label: "Lunch", match: "MID_DAY" },
    { label: "High Tea", match: "EVENING" },
    { label: "Dinner", match: "NIGHT" },
  ];

  // Toggle venue expansion
  const toggleVenueExpansion = (venueName: string) => {
    setExpandedVenues((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(venueName)) {
        newSet.delete(venueName);
      } else {
        newSet.add(venueName);
      }
      return newSet;
    });
  };

  // Generate compact view text for a date - SHOW BOOKED SHIFTS
  const getCompactViewText = (dateData: any[]) => {
    const shiftLetterMap: Record<string, string> = {
      DAY: "F",
      MORNING: "B",
      MID_DAY: "L",
      EVENING: "H",
      NIGHT: "D",
    };

    // Get all BOOKED shifts and sort them in order: B, L, H, D
    const bookedShifts = dateData
      .filter(
        (shift) =>
          shift.status === "BOOKED" ||
          shift.status === "TENTATIVE_HOLD" ||
          shift.status === "HOLD" ||
          shift.status === "ONGOING",
      )
      .map((shift) => shiftLetterMap[shift.shift_time])
      .filter(Boolean);

    // Sort in the order: B, L, H, D
    const order = ["B", "L", "H", "D"];
    const sortedShifts = bookedShifts.sort(
      (a, b) => order.indexOf(a) - order.indexOf(b),
    );

    return sortedShifts.join("") || "-";
  };

  // Get the dominant status for a compact view cell
  const getCompactCellStatus = (dateData: any[]) => {
    // Show the highest-priority status present on the date
    const priority = ["BOOKED", "HOLD", "TENTATIVE_HOLD", "ONGOING"];
    const found = priority.find((s) =>
      dateData.some((shift) => shift.status === s),
    );
    return found ?? "";
  };

  React.useEffect(() => {
    if (!inventoryData || !tableRef.current) return;

    const firstVenue = Object.keys(inventoryData)[0];
    if (!firstVenue) return;

    const dates = Object.keys(inventoryData[firstVenue].dates);

    const today = moment().format("YYYY-MM-DD");
    const selectedMonth = values.month;
    const selectedYear = values.year;

    const currentMonth = moment().format("MM");
    const currentYear = moment().format("YYYY");

    const container = tableRef.current;
    const headerCells = container.querySelectorAll("thead tr:nth-child(2) th");

    if (!headerCells || headerCells.length === 0) return;

    const getScrollLeftForIndex = (index: number) => {
      const cell = headerCells[index] as HTMLElement | undefined;
      if (!cell) return 0;
      const containerWidth = container.clientWidth;
      return cell.offsetLeft - containerWidth / 2 + cell.offsetWidth / 2;
    };

    let targetIndex = 0;
    /** When set with event focus: scroll from this column (today) to target for a visible “present → event” motion */
    let scrollFromIndex: number | null = null;

    if (eventFocusDateKey) {
      const focusIdx = dates.findIndex((d) => d === eventFocusDateKey);
      if (focusIdx !== -1) {
        targetIndex = focusIdx;
        const todayIdx = dates.findIndex((d) => d === today);
        if (todayIdx !== -1 && todayIdx !== focusIdx) {
          scrollFromIndex = todayIdx;
        }
      } else if (
        selectedMonth === currentMonth &&
        selectedYear === currentYear
      ) {
        const todayIndex = dates.findIndex((d) => d === today);
        if (todayIndex !== -1) {
          targetIndex = todayIndex;
        }
      }
    } else if (selectedMonth === currentMonth && selectedYear === currentYear) {
      const todayIndex = dates.findIndex((d) => d === today);
      if (todayIndex !== -1) {
        targetIndex = todayIndex;
      }
    }

    let timeoutId: number | undefined;

    const run = () => {
      if (scrollFromIndex !== null) {
        container.scrollTo({
          left: getScrollLeftForIndex(scrollFromIndex),
          behavior: "auto",
        });
        timeoutId = window.setTimeout(() => {
          container.scrollTo({
            left: getScrollLeftForIndex(targetIndex),
            behavior: "smooth",
          });
        }, 150);
      } else {
        container.scrollTo({
          left: getScrollLeftForIndex(targetIndex),
          behavior: "smooth",
        });
      }
    };

    const rafId = requestAnimationFrame(run);

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [inventoryData, values.month, values.year, eventFocusDateKey]);

  React.useEffect(() => {
    if (
      eventLeadData?.preferred_venue_uuid &&
      inventoryData &&
      Object.keys(inventoryData).length > 0
    ) {
      // venue_uuid is now the key directly
      if (inventoryData[eventLeadData.preferred_venue_uuid]) {
        setExpandedVenues(new Set([eventLeadData.preferred_venue_uuid]));
      }
    }
  }, [inventoryData, eventLeadData]);

  return (
    <>
      <StandardCard
        heading={isFromEventLead ? "" : "Inventory Report"}
        sx={{ mb: 2 }}
      >
        <Stack spacing={3}>
          {/* Legend Section */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
          >
            {/* Status Legend */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                flexWrap: "wrap", // Added wrap
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                Status Colors:
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {[
                  { label: "Available", color: statusColors.AVAILABLE },
                  { label: "Booked", color: statusColors.BOOKED },
                  { label: "Hold", color: statusColors.HOLD },
                  { label: "Tentative Hold", color: statusColors.TENTATIVE_HOLD },
                  { label: "Ongoing", color: statusColors.ONGOING },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 0.5,
                        backgroundColor: item.color,
                        border: "1px solid #404040",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Shift Types Legend */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mr: 0.5,
                }}
              >
                Shifts:
              </Typography>
              {[
                { letter: "F", name: "Full Day" },
                { letter: "B", name: "Breakfast" },
                { letter: "L", name: "Lunch" },
                { letter: "H", name: "High Tea" },
                { letter: "D", name: "Dinner" },
              ].map((shift, index) => (
                <React.Fragment key={shift.letter}>
                  <Tooltip title={shift.name} arrow placement="top">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "help",
                      }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 0.5,
                          backgroundColor: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                        }}
                      >
                        {shift.letter}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: 500,
                        }}
                      >
                        {shift.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                  {index < 4 && (
                    <Box
                      sx={{
                        width: "1px",
                        height: "16px",
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        display: { xs: "none", sm: "block" },
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Stack>
        </Stack>
      </StandardCard>

      <StandardCard>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              {!isMobile && <CustomFormLabel>Select Venue</CustomFormLabel>}
              <VenueAutoSearch
                value={values.venueId}
                onSelect={(data) => setFieldValue("venueId", data.venue_uuid)}
                {...(isMobile && {
                  label: "Select Venue",
                  InputLabelProps: { shrink: true },
                })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {!isMobile && <CustomFormLabel>Select Month</CustomFormLabel>}
              <ControlledCustomSelect
                sx={{ minWidth: 150 }}
                name="month"
                options={months_list}
                value={values.month}
                onChange={handleChange}
                displayEmpty
                fullWidth
                placeholder="Select one"
                error={errors.month ? true : false}
                helperText={errors.month as string}
                {...(isMobile && {
                  label: "Select Month",
                  InputLabelProps: { shrink: true },
                })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {isMobile ? (
                <ControlledCustomSelect
                  sx={{ minWidth: 150 }}
                  name="year"
                  options={yearOptionsList.map((y) => ({
                    label: y,
                    value: String(y),
                  }))}
                  value={values.year}
                  onChange={handleChange}
                  displayEmpty
                  fullWidth
                  placeholder="Select one"
                  error={errors.year ? true : false}
                  helperText={errors.year as string}
                  label="Select Year"
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <CustomSelectorWithLabel
                  sx={{ minWidth: 150 }}
                  label="Select Year"
                  name="year"
                  fullWidth
                  options={yearOptionsList}
                  value={values.year}
                  onChange={handleChange}
                  displayEmpty
                  placeholder="Select one"
                  error={errors.year as string}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: "flex", gap: 2 }}>
              {/* <Button
                fullWidth
                variant="contained"
                sx={{ mt: 5 }}
                type="submit"
              >
                Search
              </Button> */}

              <Button
                variant="contained"
                sx={{ mt: 5 }}
                onClick={handleClearFilters}
                fullWidth
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </form>
        {inventoryData && Object.keys(inventoryData).length > 0 ? (
          <Box sx={{}}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ color: "#fff" }}>
                Search Results
              </Typography>
              <CheckCircleIcon sx={{ color: "#fff" }} />
            </Box>

            <CustomTableContainer
              ref={tableRef}
              sx={{ minWidth: 650, maxHeight: "70vh", overflow: "auto" }}
            >
              {(() => {
                const firstVenueKey = Object.keys(inventoryData)[0];
                const allDates = Object.keys(inventoryData[firstVenueKey].dates);
                if (allDates.length === 0) return null;

                return (
              <Table
                size="small"
                aria-label="a dense table"
                sx={{ position: "relative" }}
              >
                <TableHead>
                  {allDates.length > 0 && (
                    <>
                      <TableRow>
                        {/* Sticky Venue header - Left & Top */}
                        <CustomHeaderTableCell
                          rowSpan={3}
                          sx={{
                            position: "sticky",
                            left: 0,
                            top: 0,
                            zIndex: 120,
                            backgroundColor: "#212121",
                          }}
                        >
                          Unit Type
                        </CustomHeaderTableCell>

                        {/* Month/Year row - Sticky Top */}
                        {Object.keys(
                          groupDataByMonth(
                            inventoryData[Object.keys(inventoryData)[0]].dates,
                          ),
                        ).map((object_key) => {
                          const columnLength = groupDataByMonth(
                            inventoryData[Object.keys(inventoryData)[0]].dates,
                          )[object_key].length;

                          return (
                            <CustomHeaderTableCell
                              key={object_key}
                              colSpan={columnLength}
                              sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 100,
                                backgroundColor: "#212121",
                              }}
                            >
                              <Typography variant="body1" textAlign={"center"}>
                                {moment(object_key).format("MMM")}
                              </Typography>
                              <Typography variant="body1" textAlign={"center"}>
                                {moment(object_key).format("YYYY")}
                              </Typography>
                            </CustomHeaderTableCell>
                          );
                        })}
                      </TableRow>

                      <TableRow>
                        {/* Date (DD) row - Sticky Top */}
                        {allDates.map((object_key) => (
                          <CustomHeaderTableCell
                            key={object_key}
                            sx={{
                              position: "sticky",
                              top: 40, // Height of first row (Month/Year)
                              zIndex: 100,
                              backgroundColor: "#212121",
                              ...getFocusColumnHeaderSx(object_key),
                            }}
                          >
                            {moment(object_key).format("DD")}
                          </CustomHeaderTableCell>
                        ))}
                      </TableRow>

                      <TableRow>
                        {/* Day of week (M/T/W) row - Sticky Top */}
                        {allDates.map((object_key) => (
                          <CustomHeaderTableCell
                            key={object_key}
                            sx={{
                              position: "sticky",
                              top: 73, // Height of first row + second row
                              zIndex: 100,
                              backgroundColor: "#212121",
                              ...getFocusColumnHeaderSx(object_key),
                            }}
                          >
                            {moment(object_key).format("dddd").charAt(0)}
                          </CustomHeaderTableCell>
                        ))}
                      </TableRow>
                    </>
                  )}
                </TableHead>

                <TableBody>
                  {allDates.length > 0 &&
                    Object.keys(inventoryData).map((venueUuid, index) => {
                      const venueEntry = inventoryData[venueUuid];
                      const dataByCategory = venueEntry.dates;
                      const venueName = venueEntry.venue_name;
                      const isExpanded = expandedVenues.has(venueUuid);

                      return (
                        <React.Fragment key={venueUuid}>
                          {/* Compact View (Default) */}
                          {!isExpanded && (
                            <TableRow>
                              {/* Sticky Venue column with expand button + pin icon + venue name */}
                              <CustomTableCell
                                sx={{
                                  minWidth: 200,
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 100,
                                  backgroundColor: "#212121",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Tooltip
                                    title={
                                      isExpanded
                                        ? "Collapse to compact view"
                                        : "Expand to detailed view"
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        toggleVenueExpansion(venueUuid)
                                      }
                                      sx={{ color: "#fff", p: 0.5 }}
                                    >
                                      {isExpanded ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <KeyboardArrowDownIcon />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  <PinDrop
                                    sx={{ fontSize: 18, color: "#fff" }}
                                  />
                                  <Typography variant="body1" fontWeight={600}>
                                    {venueName}
                                  </Typography>
                                </Stack>
                              </CustomTableCell>

                              {/* Dynamic Columns - Compact */}
                              {allDates.map((object_key) => {
                                const dateData = dataByCategory[object_key];
                                if (!dateData) {
                                  return (
                                    <CustomTableCell
                                      key={object_key}
                                      sx={{
                                        border: "1px solid #404040",
                                        backgroundColor: "#303030",
                                        textAlign: "center",
                                      }}
                                    >
                                      -
                                    </CustomTableCell>
                                  );
                                }
                                const compactText =
                                  getCompactViewText(dateData);
                                const compactStatus =
                                  getCompactCellStatus(dateData);
                                const cellColor = getStatusColor(compactStatus);

                                return (
                                  <CustomTableCell
                                    key={object_key}
                                    sx={{
                                      cursor: "pointer",
                                      border: "1px solid #404040",
                                      backgroundColor: cellColor,
                                      color: getStatusTextColor(compactStatus),
                                      fontWeight: 500,
                                      textAlign: "center",
                                      ...getFocusColumnCellSx(
                                        object_key,
                                        cellColor,
                                      ),
                                    }}
                                    onClick={() => {
                                      // Expand the venue to show details (for any date cell click)
                                      toggleVenueExpansion(venueUuid);
                                    }}
                                  >
                                    {compactText}
                                  </CustomTableCell>
                                );
                              })}
                            </TableRow>
                          )}

                          {/* Expanded View (Detailed) */}
                          {isExpanded && (
                            <>
                              {/* Venue Header Row - First Row with collapse button + pin + venue name */}
                              <TableRow>
                                <CustomTableCell
                                  sx={{
                                    minWidth: 200,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 100,
                                    backgroundColor: "#212121",
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Tooltip title="Collapse to compact view">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          toggleVenueExpansion(venueUuid)
                                        }
                                        sx={{ color: "#fff", p: 0.5 }}
                                      >
                                        <KeyboardArrowUpIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <PinDrop
                                      sx={{ fontSize: 18, color: "#fff" }}
                                    />
                                    <Typography
                                      variant="body1"
                                      fontWeight={600}
                                    >
                                      {venueName}
                                    </Typography>
                                  </Stack>
                                </CustomTableCell>

                                {/* Date columns with "-" for venue header row */}
                                {allDates.map(
                                  (object_key) => (
                                    <CustomTableCell
                                      key={object_key}
                                      sx={{
                                        border: "1px solid #404040",
                                        backgroundColor: "#303030",
                                        textAlign: "center",
                                        ...getFocusColumnCellSx(
                                          object_key,
                                          "#303030",
                                        ),
                                      }}
                                    ></CustomTableCell>
                                  ),
                                )}
                              </TableRow>

                              {/* Shift Rows - Full Day, Breakfast, Lunch, High Tea, Dinner */}
                              {hardcodedShifts.map((shift, hotel_idx) => {
                                const formattedName = shift.label;
                                const shiftLetterMap: Record<string, string> = {
                                  "Full Day": "F",
                                  Breakfast: "B",
                                  Lunch: "L",
                                  "High Tea": "H",
                                  Dinner: "D",
                                };

                                return (
                                  <TableRow key={hotel_idx}>
                                    {/* Sticky Unit Type column - show shift name */}
                                    <CustomTableCell
                                      sx={{
                                        minWidth: 200,
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 100,
                                        backgroundColor: "#212121",
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={600}
                                      >
                                        {formattedName}
                                      </Typography>
                                    </CustomTableCell>

                                    {/* Dynamic Columns */}
                                    {allDates.map(
                                      (object_key) => {
                                        const dateItems = dataByCategory[object_key];
                                        const current_column_data = dateItems
                                          ? dateItems.find(
                                            (x: any) =>
                                              x.shift_time === shift.match,
                                          )
                                          : undefined;

                                        const status =
                                          current_column_data?.status ?? "";
                                        const available_room =
                                          shiftLetterMap[formattedName] || "A";
                                        const shiftCellBg =
                                          getStatusColor(status);

                                        return (
                                          <CustomTableCell
                                            key={object_key + hotel_idx}
                                            sx={{
                                              cursor:
                                                current_column_data?.occupancy_uuid ||
                                                onSelectevent ||
                                                current_column_data?.status ===
                                                  "ONGOING"
                                                  ? "pointer"
                                                  : "default",
                                              border: "1px solid #404040",
                                              backgroundColor: shiftCellBg,
                                              color: getStatusTextColor(status),
                                              fontWeight:
                                                status === "BOOKED" ? 600 : 400,
                                              ...getFocusColumnCellSx(
                                                object_key,
                                                shiftCellBg,
                                              ),
                                            }}
                                            onClick={(event) => {
                                              if (
                                                current_column_data?.occupancy_uuid &&
                                                current_column_data?.status !==
                                                  "ONGOING"
                                              ) {
                                                setOpenView(
                                                  current_column_data.occupancy_uuid,
                                                );
                                              } else if (
                                                current_column_data?.status ===
                                                  "ONGOING" &&
                                                current_column_data?.leads
                                                  ?.length > 0
                                              ) {
                                                if (!onSelectevent) {
                                                  setOpenOngoingDialog(
                                                    current_column_data?.leads ||
                                                      [],
                                                  );
                                                } else {
                                                  setAnchorEl(
                                                    event.currentTarget,
                                                  );
                                                  setSelectedCell(
                                                    current_column_data,
                                                  );
                                                }
                                              } else if (onSelectevent) {
                                                const column =
                                                  current_column_data as IInventoryColumnData;
                                                let extractedMonth = "";
                                                if (column.calendar_date) {
                                                  const date = new Date(
                                                    column.calendar_date,
                                                  );
                                                  extractedMonth =
                                                    date.toLocaleString(
                                                      "default",
                                                      {
                                                        month: "long",
                                                      },
                                                    );
                                                }

                                                onSelectevent({
                                                  function_date:
                                                    column.calendar_date,
                                                  event_time_uuid:
                                                    column.event_timing_uuid,
                                                  event_time: column.event_name,
                                                  preferred_venue_uuid:
                                                    column.venue_uuid,
                                                  preferred_venue:
                                                    column.venue_name,
                                                  function_month:
                                                    extractedMonth,
                                                });
                                              }
                                            }}
                                          >
                                            {`${shiftLetterMap[formattedName]}${current_column_data?.status === "ONGOING" && current_column_data?.lead_count ? `${current_column_data?.lead_count}` : ""}`.trim()}
                                          </CustomTableCell>
                                        );
                                      },
                                    )}
                                  </TableRow>
                                );
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
                );
              })()}
            </CustomTableContainer>
          </Box>
        ) : (
          <NoRecordFound description="We couldn't find any records." />
        )}
        {/* {inventoryData &&
          Object.keys(inventoryData)
            .slice(0, 2)
            .map((catgoryKey, index) => {
              const dataByCategory = inventoryData[catgoryKey];
              return (
                <>
                  {dataByCategory && Object.keys(dataByCategory).length > 0 && (
                    <Box sx={{}}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="h6" sx={{ color: "#fff" }}>
                          Search Results
                        </Typography>
                        <CheckCircleIcon sx={{ color: "#fff" }} />
                      </Box>

                      <CustomTableContainer>
                        <Table
                          sx={{ minWidth: 650 }}
                          size="small"
                          aria-label="a dense table"
                        >
                          <TableBody>
                            <TableRow>
                              <CustomTableCell rowSpan={3}>
                                Unit Type
                              </CustomTableCell>
                              {Object.keys(
                                groupDataByMonth(dataByCategory)
                              ).map((object_key) => {
                                const columnLength =
                                  groupDataByMonth(dataByCategory)[object_key]
                                    .length;
                                return (
                                  <CustomTableCell
                                    key={object_key}
                                    colSpan={columnLength}
                                  >
                                    <Typography
                                      variant="body1"
                                      textAlign={"center"}
                                    >
                                      {moment(object_key).format("MMM")}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      textAlign={"center"}
                                    >
                                      {moment(object_key).format("YYYY")}
                                    </Typography>
                                  </CustomTableCell>
                                );
                              })}
                            </TableRow>
                            <TableRow>
                              {Object.keys(dataByCategory).map((object_key) => (
                                <CustomTableCell key={object_key}>
                                  {moment(object_key).format("DD")}
                                </CustomTableCell>
                              ))}
                            </TableRow>
                            <TableRow>
                              {Object.keys(dataByCategory).map((object_key) => (
                                <CustomTableCell key={object_key}>
                                  {moment(object_key).format("dddd").charAt(0)}
                                </CustomTableCell>
                              ))}
                            </TableRow>

                            {dataByCategory[Object.keys(dataByCategory)[0]]
                              .length > 0 && (
                              <TableRow>
                                <CustomTableCell
                                  colSpan={
                                    Object.keys(dataByCategory).length + 3
                                  }
                                >
                                  <Typography variant="h6">
                                    {
                                      dataByCategory[
                                        Object.keys(dataByCategory)[0]
                                      ][0]["venue_name"]
                                    }
                                  </Typography>
                                </CustomTableCell>
                              </TableRow>
                            )}

                            {(
                              dataByCategory[Object.keys(dataByCategory)[0]] ||
                              []
                            ).map((hotel: any, hotel_idx: any) => {
                              const hotelName = hotel["shift_time"];
                              return (
                                <TableRow key={hotel_idx}>
                                  <CustomTableCell sx={{ minWidth: 120 }}>
                                    <Typography variant="body1">
                                      {formatText(hotelName)
                                        .toLowerCase()
                                        .replace(/^\w/, (c) => c.toUpperCase())}
                                    </Typography>
                                  </CustomTableCell>

                                  {Object.keys(dataByCategory).map(
                                    (object_key) => {
                                      const current_column_data =
                                        dataByCategory[object_key].find(
                                          (x: any) => x.shift_time === hotelName
                                        );
                                      const available_room =
                                        current_column_data["status"] !==
                                        "BOOKED"
                                          ? "A"
                                          : "B";

                                      return (
                                        <CustomTableCell
                                          key={object_key + hotel_idx}
                                          sx={{
                                            cursor: "pointer",
                                            backgroundColor:
                                              available_room !== "A"
                                                ? theme.palette.success.main
                                                : "#222222",
                                            fontWeight:
                                              available_room !== "A"
                                                ? 600
                                                : 400,
                                          }}
                                          onClick={() =>
                                            setOpenView(
                                              current_column_data[
                                                "occupancy_uuid"
                                              ]
                                            )
                                          }
                                        >
                                          {available_room}
                                        </CustomTableCell>
                                      );
                                    }
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CustomTableContainer>
                    </Box>
                  )}
                </>
              );
            })} */}
      </StandardCard>

      {selectedCell && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box sx={{ px: 2, py: 1, fontSize: 12, color: "#888" }}>
            Leads: {selectedCell?.lead_count || 0}
          </Box>

          <Divider />

          <List dense>
            <ListItemButton
              onClick={() => {
                handleClose();
                setOpenOngoingDialog(selectedCell?.leads || []);
              }}
            >
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View Leads" />
            </ListItemButton>

            {/* ✅ SELECT */}
            <ListItemButton
              onClick={() => {
                handleClose();

                if (onSelectevent && selectedCell) {
                  let extractedMonth = "";

                  if (selectedCell.calendar_date) {
                    const date = new Date(selectedCell.calendar_date);
                    extractedMonth = date.toLocaleString("default", {
                      month: "long",
                    });
                  }

                  onSelectevent({
                    function_date: selectedCell.calendar_date,
                    function_month: extractedMonth,
                    event_time_uuid: selectedCell.event_timing_uuid,
                    event_time: selectedCell.event_name,
                    preferred_venue_uuid: selectedCell.venue_uuid,
                    preferred_venue: selectedCell.venue_name,
                  });
                }
              }}
            >
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Select Event" />
            </ListItemButton>
          </List>
        </Popover>
      )}

      {openView && (
        <ViewOccupancyRightPanel
          open={true}
          id={openView}
          onClose={() => setOpenView(null)}
        />
      )}
      {openOngoingDialog && (
        <ViewOngoingLeadDialog
          open={true}
          leads={openOngoingDialog}
          onClose={() => setOpenOngoingDialog(null)}
        />
      )}
    </>
  );
};
